import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import StatusPill, { CASE_STATUS_PILL } from '../components/StatusPill';
import { ArrowRight, Clock, Cross, Doc, Padlock, Person, Plus, Tick } from '../components/AppIcons';
import { Brand, brandify } from '../../components/Brand';
import { EXPERTS, getExpert } from '../data';
import { useActiveCompany } from '../state';
import { updateCase } from '../../lib/entities/cases';
import { listEntityQuestions, type EntityQuestionValue } from '../../lib/entities/entityQuestions';
import EntityChecklistSection from '../components/EntityChecklistSection';
import EntityQuestionGate from '../components/EntityQuestionGate';
import type { CaseRisk, CaseStatus, CaseStep, CompanyDocument, Expert, HrCase } from '../types';
import { RISK_PILL } from './CasesPage';
import '../../styles/app-cases.css';
import '../../styles/app-employees.css'; // .emp-filelink in the header meta

/** A line of colour on top of the data: the expert's most recent message. */
const LATEST_UPDATE: Record<string, string> = {
  'grievance-kitchen': 'Investigation plan ready for your sign-off, sent today 08:47.',
};

function expertById(id: string): Expert {
  return EXPERTS.find((e) => e.id === id) ?? EXPERTS[0];
}

/** Preselect the expert whose specialisms best match the kind of case. */
export function bestFitExpert(kind: string): Expert {
  const k = kind.toLowerCase();
  if (/leaver|offboard|redundan|restructur|settlement/.test(k)) return expertById('daniel-osei');
  if (/family|flexible|contract/.test(k)) return expertById('meera-krishnan');
  return expertById('eleanor-whitfield');
}

/** "Replies within 4 working hours" → "They’ll reply within 4 working hours". */
function replyLine(expert: Expert): string {
  return /^replies/i.test(expert.responseTime)
    ? expert.responseTime.replace(/^replies/i, 'They’ll reply')
    : `They’ll reply, ${expert.responseTime.toLowerCase()}`;
}

function ExpertPanel({ expert, update, justEscalated }: { expert: Expert; update: string; justEscalated: boolean }) {
  return (
    <div className="case-expert">
      <div className="case-expert__head">
        <span className="case-expert__label">
          <Person size={14} />
          Your expert
        </span>
        {justEscalated && <span className="case-expert__flash">Escalated just now</span>}
      </div>
      <div className="case-expert__who">
        <span className="case-expert__avatar">{expert.initials}</span>
        <div>
          <div className="case-expert__name">{expert.name}</div>
          <div className="case-expert__creds">{expert.credentials}</div>
        </div>
      </div>
      <div className="case-expert__tags">
        {expert.specialisms.map((s) => (
          <span key={s} className="case-expert__tag">
            {s}
          </span>
        ))}
      </div>
      <div className="case-expert__resp">
        <Clock size={14} />
        {expert.responseTime}
      </div>
      <div className="case-expert__update">
        <span className="case-expert__update-label">Latest update</span>
        {update}
      </div>
    </div>
  );
}

function CaseDetail({ hrCase }: { hrCase: HrCase }) {
  const { employees, documents, refetchCases } = useActiveCompany();
  const [localExpertId, setLocalExpertId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string>('');
  const [note, setNote] = useState('');

  // Lightweight case editor: status/risk/next step/timeline, saved via the API.
  const [editing, setEditing] = useState(false);
  const [draftStatus, setDraftStatus] = useState<CaseStatus>(hrCase.status);
  const [draftRisk, setDraftRisk] = useState<CaseRisk>(hrCase.risk);
  const [draftNextStep, setDraftNextStep] = useState('');
  const [draftSteps, setDraftSteps] = useState<CaseStep[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Captured per-case predicate answers, editable alongside the rest of the case.
  const [questions, setQuestions] = useState<EntityQuestionValue[]>([]);
  const [answers, setAnswers] = useState<Record<string, boolean | null>>({});
  // Answering a per-case question changes the resolved tiers, so bump this to re-resolve the section.
  const [checklistReload, setChecklistReload] = useState(0);
  // Only true once the question list actually loaded. A save must NOT send `predicates` before then:
  // the server treats a present-but-empty map as replace-all, which would wipe the stored answers.
  const [questionsLoaded, setQuestionsLoaded] = useState(false);

  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setModalOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [modalOpen]);

  const startEdit = () => {
    setDraftStatus(hrCase.status);
    setDraftRisk(hrCase.risk);
    setDraftNextStep(hrCase.nextStep ?? '');
    setDraftSteps(hrCase.steps.map((s) => ({ ...s })));
    setSaveError(null);
    setEditing(true);
    setQuestions([]);
    setAnswers({});
    setQuestionsLoaded(false);
    // Applicable questions are resolved server-side from the case's own answers, so just read them.
    void listEntityQuestions('/api/cases', hrCase.id)
      .then((list) => {
        setQuestions(list.questions);
        setAnswers(Object.fromEntries(list.questions.map((q) => [q.key, q.value])));
        setQuestionsLoaded(true);
      })
      .catch(() => {
        // A failed question load must not block editing the rest of the case, and must not let the
        // save send an empty predicates map, so the flag stays false and the key is omitted entirely.
        setQuestions([]);
        setQuestionsLoaded(false);
      });
  };

  const updateStep = (index: number, patch: Partial<CaseStep>) => {
    setDraftSteps((steps) => steps.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  };
  const addStep = () => {
    setDraftSteps((steps) => [...steps, { title: '', detail: '', state: 'todo' }]);
  };
  const removeStep = (index: number) => {
    setDraftSteps((steps) => steps.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      // Only answered questions are sent; the server replaces the map with exactly this and re-applies
      // the parent gate, so a child orphaned by a parent going false drops out server-side.
      const predicates: Record<string, boolean> = {};
      for (const q of questions) {
        const value = answers[q.key];
        if (typeof value === 'boolean') predicates[q.key] = value;
      }
      // Which questions were on screen BEFORE this save; only keys that are new afterwards count as
      // newly unlocked (a child a just-answered parent revealed). Treating "any unanswered" as unlocked
      // would trap the editor open; unanswered questions are instead gated by `saveBlocked`.
      const previousKeys = new Set(questions.map((q) => q.key));

      await updateCase(hrCase.id, {
        status: draftStatus,
        risk: draftRisk,
        nextStep: draftNextStep.trim(),
        steps: draftSteps
          .filter((s) => s.title.trim())
          .map((s) => ({
            title: s.title.trim(),
            detail: s.detail?.trim() ? s.detail.trim() : undefined,
            state: s.state,
          })),
        // Omitted entirely when the questions never loaded: omitted means "leave untouched", an empty
        // object would mean "replace with nothing" and destroy the captured answers.
        predicates: questionsLoaded ? predicates : undefined,
      });
      await refetchCases();
      // An answer may have moved an obligation's tier, so re-resolve the case checklist.
      setChecklistReload((n) => n + 1);

      // An answer can unlock child questions; stay in the editor when it did, so they can be answered.
      if (questionsLoaded) {
        const next = await listEntityQuestions('/api/cases', hrCase.id);
        const newlyUnlocked = next.questions.filter(
          (q) => !previousKeys.has(q.key) && q.value === null,
        );
        if (newlyUnlocked.length > 0) {
          setQuestions(next.questions);
          setAnswers(Object.fromEntries(next.questions.map((q) => [q.key, q.value])));
          return;
        }
      }
      setEditing(false);
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : 'We couldn’t save your changes. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  };

  // Every applicable per-case predicate must be answered yes/no before saving: an unanswered one stores
  // as absent, which the resolver reads as false and shows the wrong tier. Only gate once the questions
  // have loaded, so a failed load does not hard-block editing the rest of the case.
  const unansweredCaseQuestions = questions.filter((q) => typeof answers[q.key] !== 'boolean');
  const saveBlocked = questionsLoaded && unansweredCaseQuestions.length > 0;

  const justEscalated = localExpertId !== null && !hrCase.expertId;
  const expertId = hrCase.expertId ?? localExpertId ?? undefined;
  const expert = expertId ? getExpert(expertId) : undefined;
  const status = justEscalated ? 'escalated' : hrCase.status;
  const statusPill = CASE_STATUS_PILL[status];
  const riskPill = RISK_PILL[hrCase.risk];
  const resolved = status === 'resolved';
  const meta = [hrCase.kind, hrCase.person, hrCase.opened].filter(Boolean).join(' · ');
  const linkedEmployee = hrCase.employeeId
    ? employees.find((e) => e.id === hrCase.employeeId)
    : undefined;

  const relatedDocs = (hrCase.relatedDocIds ?? [])
    .map((id) => documents.find((d) => d.id === id))
    .filter((d): d is CompanyDocument => d !== undefined);

  const bestFit = bestFitExpert(hrCase.kind);
  const selectedExpert = EXPERTS.find((e) => e.id === selectedId) ?? bestFit;

  const updateLine = expert
    ? justEscalated
      ? `${replyLine(expert)}. Your note has gone across with the full case file.`
      : LATEST_UPDATE[hrCase.id] ?? `${replyLine(expert)}.`
    : '';

  const openEscalate = () => {
    setSelectedId(bestFit.id);
    setNote(hrCase.summary);
    setModalOpen(true);
  };

  const confirmEscalate = () => {
    setLocalExpertId(selectedExpert.id);
    setModalOpen(false);
  };

  return (
    <div className="apage">
      <Link to="/app/cases" className="case-back">
        ← All cases
      </Link>
      <PageHeader
        eyebrow="Case"
        title={hrCase.title}
        lead={
          linkedEmployee ? (
            <>
              {meta}{' '}
              <Link to={`/app/employees/${linkedEmployee.id}`} className="emp-filelink">
                View {linkedEmployee.name.split(' ')[0]}’s file →
              </Link>
            </>
          ) : (
            meta
          )
        }
        actions={
          <>
            <StatusPill variant={statusPill.variant}>{statusPill.label}</StatusPill>
            <StatusPill variant={riskPill.variant}>{riskPill.label}</StatusPill>
            {!editing && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={startEdit}>
                Update case
              </button>
            )}
          </>
        }
      />

      {/* Force any per-case predicate left unanswered (e.g. capture abandoned) to be completed on load. */}
      <EntityQuestionGate
        base="/api/cases"
        id={hrCase.id}
        lead={
          <>Before working on this case, a couple of required questions. Each needs a yes or no.</>
        }
        onCompleted={() => {
          void refetchCases();
          setChecklistReload((n) => n + 1);
        }}
      />

      <div className="case-grid">
        <div className="case-col">
          {editing && (
            <section className="acard acard--pad">
              <h2 className="acard__title">Update this case</h2>
              <p className="acard__sub">
                Move where things stand, refine the next safe step, or adjust the timeline. Saved
                straight to your case file.
              </p>
              <div className="emp-form__row">
                <label className="field">
                  <span className="field__label">Status</span>
                  <select
                    className="select"
                    value={draftStatus}
                    onChange={(e) => setDraftStatus(e.target.value as CaseStatus)}
                  >
                    <option value="open">In progress</option>
                    <option value="waiting">Waiting</option>
                    <option value="escalated">With expert</option>
                    <option value="resolved">Resolved</option>
                  </select>
                </label>
                <label className="field">
                  <span className="field__label">Risk</span>
                  <select
                    className="select"
                    value={draftRisk}
                    onChange={(e) => setDraftRisk(e.target.value as CaseRisk)}
                  >
                    <option value="low">Low risk</option>
                    <option value="medium">Medium risk</option>
                    <option value="high">High risk</option>
                  </select>
                </label>
              </div>
              <label className="field">
                <span className="field__label">Next safe step</span>
                <textarea
                  className="textarea"
                  value={draftNextStep}
                  placeholder="The one thing to do next."
                  onChange={(e) => setDraftNextStep(e.target.value)}
                />
              </label>
              <div className="field">
                <span className="field__label">Step by step</span>
                {draftSteps.map((step, i) => (
                  <div key={i} className="case-editstep">
                    <div className="emp-form__row">
                      <label className="field">
                        <span className="field__label">Step {i + 1}</span>
                        <input
                          className="input"
                          value={step.title}
                          placeholder="What happens at this step"
                          onChange={(e) => updateStep(i, { title: e.target.value })}
                        />
                      </label>
                      <label className="field">
                        <span className="field__label">State</span>
                        <select
                          className="select"
                          value={step.state}
                          onChange={(e) =>
                            updateStep(i, { state: e.target.value as CaseStep['state'] })
                          }
                        >
                          <option value="todo">To do</option>
                          <option value="current">Current</option>
                          <option value="done">Done</option>
                        </select>
                      </label>
                    </div>
                    <label className="field">
                      <span className="field__label">Detail</span>
                      <input
                        className="input"
                        value={step.detail ?? ''}
                        placeholder="A line of context (optional)"
                        onChange={(e) => updateStep(i, { detail: e.target.value })}
                      />
                    </label>
                    <button
                      type="button"
                      className="btn btn-secondary btn-xs"
                      onClick={() => removeStep(i)}
                    >
                      Remove step
                    </button>
                  </div>
                ))}
                <button type="button" className="btn btn-secondary btn-xs" onClick={addStep}>
                  <Plus size={14} />
                  Add a step
                </button>
              </div>
              {questions.length > 0 && (
                <fieldset style={{ border: 0, padding: 0, margin: '14px 0 0' }}>
                  <legend className="field__label" style={{ padding: 0, marginBottom: 6 }}>
                    About this case
                  </legend>
                  <p className="acard__sub" style={{ margin: '0 0 8px' }}>
                    These shape which documents your business needs. Change one and the requirements
                    follow.
                  </p>
                  {questions.map((q) => (
                    <div
                      key={q.key}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                        padding: '7px 0',
                      }}
                    >
                      <span style={{ fontSize: '13.5px' }}>{q.prompt}</span>
                      <span
                        style={{ display: 'flex', gap: 6, flexShrink: 0 }}
                        role="group"
                        aria-label={q.prompt}
                      >
                        <button
                          type="button"
                          className={`btn btn-sm ${answers[q.key] === true ? 'btn-primary' : 'btn-secondary'}`}
                          onClick={() => setAnswers((prev) => ({ ...prev, [q.key]: true }))}
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          className={`btn btn-sm ${answers[q.key] === false ? 'btn-primary' : 'btn-secondary'}`}
                          onClick={() => setAnswers((prev) => ({ ...prev, [q.key]: false }))}
                        >
                          No
                        </button>
                      </span>
                    </div>
                  ))}
                </fieldset>
              )}
              {saveError && (
                <p className="acard__sub" role="alert">
                  {saveError}
                </p>
              )}
              {saveBlocked && !saveError && (
                <p className="acard__sub">Answer every question above to save.</p>
              )}
              <div className="case-modal__foot">
                <button
                  type="button"
                  className="btn btn-secondary btn-xs"
                  onClick={() => setEditing(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-xs"
                  onClick={handleSave}
                  disabled={saving || saveBlocked}
                >
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </section>
          )}

          {resolved ? (
            <div className="case-next">
              <span className="case-next__icon">
                <Tick size={16} color="#fff" />
              </span>
              <div>
                <span className="case-next__label">Resolved</span>
                <p className="case-next__text">{brandify(hrCase.summary)}</p>
              </div>
            </div>
          ) : (
            hrCase.nextStep && (
              <div className="case-next">
                <span className="case-next__icon">
                  <ArrowRight size={16} color="#fff" />
                </span>
                <div>
                  <span className="case-next__label">Next safe step</span>
                  <p className="case-next__text">{brandify(hrCase.nextStep)}</p>
                </div>
              </div>
            )
          )}

          <section className="acard acard--pad">
            <h2 className="acard__title">Step by step</h2>
            <p className="acard__sub">
              {resolved
                ? 'How it played out, kept on file for your records.'
                : brandify(hrCase.summary)}
            </p>
            <ol className="case-steps">
              {hrCase.steps.map((step, i) => (
                <li key={i} className={`case-step case-step--${step.state}`}>
                  <span className="case-step__mark">
                    {step.state === 'done' && <Tick size={13} color="#fff" />}
                    {step.state === 'current' && <span className="case-step__dot" />}
                  </span>
                  <div className="case-step__body">
                    <div className="case-step__title">
                      {brandify(step.title)}
                      {step.state === 'current' && <span className="case-step__now">You are here</span>}
                    </div>
                    {step.detail && <div className="case-step__detail">{brandify(step.detail)}</div>}
                  </div>
                </li>
              ))}
            </ol>
          </section>
        </div>

        <div className="case-col">
          {expert ? (
            <ExpertPanel expert={expert} update={updateLine} justEscalated={justEscalated} />
          ) : (
            !resolved && (
              <section className="acard acard--pad">
                <span className="case-bring__icon">
                  <Person size={20} />
                </span>
                <h2 className="acard__title">Bring in an expert</h2>
                <p className="acard__sub">
                  For when you want a human to own it with you: a CIPD-qualified specialist picks
                  this case up, sees it through, and keeps you in the loop.
                </p>
                <button type="button" className="btn btn-rose-soft btn-sm case-bring__btn" onClick={openEscalate}>
                  Escalate this case
                </button>
              </section>
            )
          )}

          {relatedDocs.length > 0 && (
            <section className="acard acard--pad">
              <h2 className="acard__title">Documents this leans on</h2>
              <p className="acard__sub">
                <Brand /> checks every step against these, so nothing contradicts what your team has
                signed.
              </p>
              <div className="case-docchips">
                {relatedDocs.map((doc) => (
                  <Link key={doc.id} to={`/app/documents/${doc.id}`} className="case-docchip">
                    <Doc size={15} />
                    {doc.name}
                  </Link>
                ))}
              </div>
            </section>
          )}

          <EntityChecklistSection
            base="/api/cases"
            id={hrCase.id}
            title="Case requirements"
            entityNoun="case"
            possessive="this case’s"
            reloadKey={checklistReload}
          />

          <p className="case-quiet">
            <Padlock size={14} />
            Kept private to your account. Nothing is shared with anyone unless you escalate.
          </p>
        </div>
      </div>

      {modalOpen && (
        <div className="modal-scrim" onClick={() => setModalOpen(false)} role="presentation">
          <div
            className="modal case-modal--wide"
            role="dialog"
            aria-modal="true"
            aria-labelledby="case-escalate-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="modal__close"
              aria-label="Close"
              onClick={() => setModalOpen(false)}
            >
              <Cross size={18} />
            </button>
            <h2 className="modal__title" id="case-escalate-title">
              Bring in an expert
            </h2>
            <p className="case-modal__sub">
              Pick who takes this on. They read the full case before replying, so you won’t have to
              repeat yourself.
            </p>
            <div className="case-expcards" role="radiogroup" aria-label="Choose an expert">
              {EXPERTS.map((e) => {
                const selected = selectedExpert.id === e.id;
                return (
                  <button
                    key={e.id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    className={`case-expcard${selected ? ' is-selected' : ''}`}
                    onClick={() => setSelectedId(e.id)}
                  >
                    <span className="case-expcard__avatar">{e.initials}</span>
                    <span className="case-expcard__body">
                      <span className="case-expcard__name">
                        {e.name}
                        {bestFit.id === e.id && <span className="case-expcard__fit">Best fit</span>}
                      </span>
                      <span className="case-expcard__creds">{e.credentials}</span>
                      <span className="case-expcard__tags">
                        {e.specialisms.map((s) => (
                          <span key={s} className="case-expcard__tag">
                            {s}
                          </span>
                        ))}
                      </span>
                      <span className="case-expcard__resp">
                        <Clock size={13} />
                        {e.responseTime}
                      </span>
                    </span>
                    <span className="case-expcard__check">
                      {selected && <Tick size={12} color="#fff" />}
                    </span>
                  </button>
                );
              })}
            </div>
            <label className="field">
              <span className="field__label">A note to go with the case</span>
              <textarea className="textarea" value={note} onChange={(e) => setNote(e.target.value)} />
            </label>
            <p className="case-modal__quiet">
              They get the full case file and your note, so there’s nothing to re-explain.
            </p>
            <div className="case-modal__foot">
              <button type="button" className="btn btn-secondary btn-xs" onClick={() => setModalOpen(false)}>
                Not now
              </button>
              <button type="button" className="btn btn-primary btn-xs" onClick={confirmEscalate}>
                Escalate to {selectedExpert.name.split(' ')[0]}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CaseDetailPage() {
  const { cases, companyLoading } = useActiveCompany();
  const { caseId } = useParams();
  const hrCase = caseId ? cases.find((c) => c.id === caseId) : undefined;

  if (!hrCase) {
    if (companyLoading) {
      return (
        <div className="apage">
          <Link to="/app/cases" className="case-back">
            ← All cases
          </Link>
          <PageHeader eyebrow="Case" title="Loading…" />
          <div className="acard">
            <div className="empty">Loading this case…</div>
          </div>
        </div>
      );
    }
    return (
      <div className="apage">
        <Link to="/app/cases" className="case-back">
          ← All cases
        </Link>
        <PageHeader eyebrow="Cases" title="We can’t find that case" />
        <div className="acard">
          <div className="empty">
            <p>This case may have been resolved and cleared, or the link is out of date.</p>
            <Link to="/app/cases" className="btn btn-secondary btn-sm case-empty__btn">
              Back to all cases
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Key by id so local state (escalation, modal, editor) resets between cases.
  return <CaseDetail key={hrCase.id} hrCase={hrCase} />;
}
